import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { recipeCopy } from 'js/src/agent/skills/recipeCopy';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';
import { MOTION_GRAPHICS_SCENE_DESIGN_ID } from 'js/src/agent/tools/motion-graphics/motionGraphicsSceneDesign';
import Flags from 'js/src/libs/flags';

export const createMotionGraphicsVideoSkill: Skill = {
    name: SKILL_NAMES.createMotionGraphicsVideo,
    description:
        'Motion graphics video — every visual is a bespoke animated graphic rather than stock ' +
        'footage. Use when stock footage or user-supplied media cannot show the content: ' +
        'numbers, comparisons, processes, diagrams, abstract concepts.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        ...recipeCopy(SKILL_NAMES.createMotionGraphicsVideo),
        preview: 'motion-graphics',
        isAvailable: () => Flags.canUseCodeo(),
    },
    get instructions(): string {
        return `# Create Motion Graphics Video

Every visual in this video is a bespoke animated graphic, built for it. There is no stock
footage and no photography — the whole video is drawn and typographic. That makes it the
format for content footage can't show: numbers, comparisons, processes, diagrams,
abstract concepts.

Load the \`${SKILL_NAMES.createVideo}\` skill too — everything there applies; this skill adds the
motion graphics formula, and its rules win where they conflict. Load
\`${SKILL_NAMES.motionGraphicsScenesBasics}\` before adding the first graphic — it has the mechanics,
and it points at the composition contract you author against.

## Set the expectation before you build
This format contains no photography or footage. If the user's material is media-led —
product shots, brand photos, footage they want on screen — say so plainly and offer
\`${SKILL_NAMES.createVideo}\` instead. Don't build a graphics video around media that can't appear
in it.

## Structure
- **One AI voiceover scene group for the whole video** (\`${SKILL_NAMES.aiVoiceoverVideoBasics}\`), then
  every visual is a graphic over it. Nothing else appears — no template designs, no stock
  media — and that includes the title and the outro.
- **This format is always voiced** — the graphics are timed to the narration. Override
  \`${SKILL_NAMES.createVideo}\` and set \`require_voiceover: true\` on the voice question,
  rather than offering the "No voiceover" choice it leaves open.
- Add them with \`${TOOL_NAMES.addVoiceoverScene}\`, \`scene_design_id\` \`"${MOTION_GRAPHICS_SCENE_DESIGN_ID}"\`, in
  ONE call listing every word range in order.
- **Every transcript word must be covered.** An uncovered span renders as raw waveform, so
  the graphics run end to end with no gaps (\`${SKILL_NAMES.voiceoverVideoBasics}\` has the coverage
  rules). Leave the base scene underneath alone.
- **Captions are OFF for this format** — the graphics do that job. Call ${TOOL_NAMES.setCaptions}
  with enabled: false right after generating the voiceover.

## Cutting the script into graphics
A graphic covers one whole idea, and that usually takes around 10 seconds — so a 60-second
video is about six graphics, not twenty. Budget the count to the target duration; there's
no fixed number. Two reasons the spans run this long, and both matter: a graphic is slow
and costly to build, and 10 seconds is what one needs to *develop* its idea rather than
state it and sit still.

**The idea boundary decides where a graphic starts and ends — not the stopwatch.** Cut at
the seams in the argument and let the blocks come out uneven: 4 seconds for one, 15 for the
next. Blocks of equal length are the tell that you cut to the clock, and cutting to the
clock slices ideas in half. Uneven is not unbounded, though — much past 15 seconds and
there is a seam you've missed.

- **Both sides of a comparison belong in the same graphic.** Where the narration sets up A
  and then B — before vs. after, the manual way vs. the automated one, one plan against
  another — don't cut between them: neither half draws it alone, because the second graphic
  can't reach back to what the first put on screen. This is the usual reason a block runs
  past 10 seconds. Where the comparison is bigger than one graphic — a whole video weighing
  two options on cost, then flexibility, then risk — split it by *dimension*, each graphic
  holding both sides of one dimension, never one graphic per side. Same for a cause and its
  effect, a claim and the figure that overturns it, a problem and its fix.
- **A title stands alone, even at 4 seconds.** The opening sentence is its own graphic.
  Don't pad it toward 10 seconds by absorbing the first real point — that costs the point
  its own graphic, and usually breaks something like a comparison to do it. Same for the
  closing line.
- Never split a block across two graphics, and never let one graphic span two unrelated
  ideas.
- **No pauses at the seams.** A settled graphic held over silence is dead air — the settle
  belongs under the voice, while the sentence is still finishing. To reach a target length,
  write more script rather than padding the narration with pauses.

<example goal="cutting a 7-sentence script into graphics">
  A script that opens by naming two options, spends two sentences on the first and two on
  the second, then closes on a recommendation, cuts into three: the opening sentence alone
  and short; all four comparison sentences in one graphic, so it holds both options and
  rearranges as the second arrives; then the close.

  Cutting it into even thirds instead — opener plus the first option, then the second
  option, then the close — is the failure. It puts the two halves of the comparison in
  different graphics, and neither can draw it.
</example>

Open on a block that says what the video is about (that's the title graphic) and close on a
real outro block.

## One idea, in phases
Plan each graphic as an ordered set of phases and build them as one continuous move, carrying
elements from each into the next. Decide, per graphic:

- **Role**: where this sits in the video's arc ("the opening title", "the third of four
  findings", "the outro").
- **Idea**: the one thing the viewer takes away.
- **Phases**: in order, each with the one piece of content it brings — the exact copy or
  number. A phase is one new thing, not a screenful. **Scale the count to the scene's
  length** — a title or closing line under about 5 seconds has room for one; a normal block
  two or three; a long one four. A scene with fewer phases than its length can carry spends
  the remainder holding still, and you build every phase you name.
- **Shape**: the structure of the content — "a single figure with its qualifier", "a
  before/after pair", "three sequential steps".
- **Tone**.

<example goal="one ~10s graphic">
  Role: the payoff, third of five.
  Idea: outages are a recurring cost, not a one-off accident.
  Phases: (1) the claim — "Every outage looks like bad luck." (2) the figure that
  overturns it — $4.2M, every 18 months.
  Shape: a claim, then the single figure that reframes it.
  Tone: sober, not alarmist.
</example>

**Phases are stages of one idea, not a second idea smuggled in.** "Every outage looks like
bad luck" → "$4.2M, every 18 months" is one idea in two phases: the second overturns the
first, so the graphic can carry the claim forward and rearrange around the figure.
"Revenue is up AND here are our four values" is two ideas, and belongs in two graphics.

## Build order
1. \`${TOOL_NAMES.loadSkill}\` \`${SKILL_NAMES.motionGraphicsScenesBasics}\`, then
   \`${SKILL_NAMES.htmlComposition}\` — **before anything else**. Between them they carry the build loop,
   the composition contract you author against, and the index of every animation technique available
   to you. Skip them and you author blind: lint rejects a composition written to rules you never
   read.
2. One \`${TOOL_NAMES.addVoiceoverScene}\` call with every word range.
3. \`${TOOL_NAMES.writeDesignSystem}\` **once, before the first plan**. Six or seven graphics
   authored one at a time only read as one video if they share a type scale, a stroke weight and a
   chart box; this is where those get decided, and every graphic afterwards is built from them.
   Build the type scale up from its smallest size, which the tool floors — every graphic inherits
   it, so a size that is too small here is wrong in all of them. This is also where the video's
   continuity is decided, before any graphic exists to drift: what recurs across graphics, and which
   seams carry an element into the next graphic instead of cutting to a fresh frame.
4. Then build the graphics **one at a time, in play order** — load that graphic's techniques, plan,
   author, capture, review, then start the next. This is the slow half of the job.

**Techniques per graphic**: one, or two when the graphic needs a second doing a different job:
one carrying the copy and one serving what the frame is made of.

## Where this overrides the standard build
These win over \`${SKILL_NAMES.createVideo}\` *and* over your standing build instructions.

- **Pacing**: ignore the ~3-4 second cap on how long anything stays on screen, along with
  "many short scenes" and "a full sentence needs two or more overlays". A graphic gets its
  ~10 seconds and develops its idea inside that span.
- **Media**: skip the scout-media step of the build arc — no stock or user media search, no
  reframing or cropping. Nothing on screen comes from a media library.
- **Designs**: no template-design selection, no design rotation, no title/listicle/outro
  design categories — this video has no template designs in it.
- **Charts**: don't add a chart asset. A figure worth seeing becomes a phase of a
  graphic. One recreated from the user's material keeps its look: say so in
  \`${TOOL_NAMES.writePlan}\`'s \`reproduces\`.
- **Captions**: off, rather than inherited.

Music still applies — add a track that fits the tone.

## Review
Load the \`${SKILL_NAMES.reviewVideo}\` skill before the review pass. You have already reviewed each
graphic's captures as you built it, so this pass is about the video as a whole:

- Every transcript word is covered — no waveform showing through a gap.
- For each graphic, the \`parts\` and \`texts\` in the video state carry the numbers and
  wording you planned, and every phase you named is actually present.
- No graphic carries two ideas, and none holds only one side of a comparison — read the
  word ranges back against the script and check that no A-versus-B, cause-and-effect or
  problem-and-fix straddles a boundary. Blocks of suspiciously even length are the signal.
- No graphic's \`texts\` repeat themselves or restate the narration over it — a line that
  says what another line or the voiceover already said comes out, unless it is the copy a handoff
  carries across a seam.
- The title and the outro are graphics, and captions are off.`;
    },
};
